import React, { useState, useContext, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useProperties from "../../hooks/useProperties";
import useGroupProperties from "../../hooks/useGroupProperties";
import useCities from "../../hooks/useCities";
import useCommunities from "../../hooks/useCommunities";
import useFileUpload from "../../hooks/useFileUpload";
import PageTransition from "./PageTransition";
import { LoadingContext } from "./Admin";

const PropertiesManager = () => {
  const navigate = useNavigate();
  const [cityId, setCityId] = useState(null);
  const [communityId, setCommunityId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { setIsPageTransition } = useContext(LoadingContext);
  
  const { cities } = useCities({ autoFetch: true });
  const { communities, fetchCommunities } = useCommunities({ cityId, autoFetch: true });
  
  const { properties, loading: propertiesLoading, error: propertiesError, fetchProperties, deleteProperty } = useProperties({ 
    cityId,
    communityId,
    autoFetch: true
  });
  
  const { groupProperties, loading: groupPropertiesLoading, error: groupPropertiesError, fetchGroupProperties, deleteGroupProperty } = useGroupProperties({
    cityId, 
    communityId,
    autoFetch: true
  });
  
  const { getFileUrl } = useFileUpload();

  // Re-fetch properties and group properties when cityId or communityId changes
  useEffect(() => {
    fetchProperties();
    fetchGroupProperties();
  }, [cityId, communityId, fetchProperties, fetchGroupProperties]);

  // Re-fetch communities when cityId changes
  useEffect(() => {
    fetchCommunities();
    // Reset communityId when cityId changes
    setCommunityId(null);
  }, [cityId, fetchCommunities]);
  
  // Combine properties and group properties into a single list with type indicator
  const combinedItems = [
    ...properties.map(prop => ({ ...prop, itemType: 'property' })),
    ...groupProperties.map(prop => ({ ...prop, itemType: 'groupProperty' }))
  ].sort((a, b) => a.name.localeCompare(b.name));

  // Smart search function
  const getInitials = (name) => {
    return name
      .split(/\s+/)
      .map(word => word.charAt(0).toLowerCase())
      .join('');
  };

  const smartSearch = (items, term) => {
    if (!term.trim()) return items;
    
    const searchTerms = term.toLowerCase().trim().split(/\s+/);
    
    return items.filter(item => {
      const itemName = item.name.toLowerCase();
      
      // 1. Full name match (case insensitive)
      if (itemName.includes(term.toLowerCase())) return true;
      
      // 2. Match each search term separately
      const allTermsMatch = searchTerms.every(term => itemName.includes(term));
      if (allTermsMatch) return true;
      
      // 3. Match by first letters of each word
      const nameWords = itemName.split(/\s+/);
      const nameInitials = getInitials(item.name);
      
      // Single search term matching initials (e.g. "db" matches "Dubai Building")
      if (searchTerms.length === 1 && nameInitials.includes(searchTerms[0])) return true;
      
      // 4. Match words that start with search terms
      const anyWordStartsWithTerm = nameWords.some(word => 
        searchTerms.some(term => word.startsWith(term))
      );
      if (anyWordStartsWithTerm) return true;
      
      return false;
    });
  };

  // Apply search filter
  const filteredItems = useMemo(() => {
    let filtered = smartSearch(combinedItems, searchTerm);
    
    // Additional frontend filtering by communityId if set
    if (communityId) {
      filtered = filtered.filter(item => {
        if (item.itemType === 'property') {
          return item.community_id === communityId;
        } else if (item.itemType === 'groupProperty') {
          return item.community_id === communityId;
        }
        return false;
      });
    }
    
    return filtered;
  }, [combinedItems, searchTerm, communityId]);

  // Modified getCityName function to properly identify city by ID or path component
  const getCityName = (cityId) => {
    if (!cityId) return '';
    
    // Try to find by ID first
    if (typeof cityId === 'number') {
      const city = cities.find(city => city.id === cityId);
      if (city) return city.name;
    }
    
    // If it's a string, it could be a path segment
    if (typeof cityId === 'string') {
      // First, check if it's a direct match with a city name
      const cityByName = cities.find(city => 
        city.name.toLowerCase() === cityId.toLowerCase());
      if (cityByName) return cityByName.name;
      
      // Then check by path
      const normalizedCityId = cityId.toLowerCase().replace(/\s+/g, '-');
      
      for (const city of cities) {
        // Extract city slug from path
        const cityPath = city.path ? 
          city.path.replace(/^\/|\/$/g, '').split('/')[0] : '';
        
        if (cityPath && cityPath.toLowerCase() === normalizedCityId) {
          return city.name;
        }
      }
      
      // If we still don't have a match, normalize and return the input
      return formatPathSegment(cityId);
    }
    
    return '';
  };

  // Enhanced getCommunityName function to properly identify community by ID or path component
  const getCommunityName = (communityId, forCityId = null) => {
    if (!communityId) return '';
    
    // Try to find by ID first
    if (typeof communityId === 'number') {
      const community = communities.find(community => community.id === communityId);
      if (community) return community.name;
    }
    
    // If it's a string, it could be a path segment
    if (typeof communityId === 'string') {
      // First, check if it's a direct match with a community name
      const communityByName = communities.find(community => 
        community.name.toLowerCase() === communityId.toLowerCase() &&
        (!forCityId || community.city_id === forCityId));
      if (communityByName) return communityByName.name;
      
      // Then check by path
      const normalizedCommunityId = communityId.toLowerCase().replace(/\s+/g, '-');
      
      for (const community of communities) {
        // Only check communities for the specified city if provided
        if (forCityId && community.city_id !== forCityId) {
          continue;
        }
        
        // Extract community slug from path
        const pathParts = community.path ? 
          community.path.replace(/^\/|\/$/g, '').split('/') : [];
        const communityPath = pathParts.length > 1 ? pathParts[1] : '';
        
        if (communityPath && communityPath.toLowerCase() === normalizedCommunityId) {
          return community.name;
        }
      }
      
      // If we still don't have a match, normalize and return the input
      return formatPathSegment(communityId);
    }
    
    return '';
  };

  // Extract path components from a full URL
  const extractPathComponents = (path) => {
    if (!path) return { city: '', community: '', propertyName: '' };
    
    try {
      // Handle both full URLs and relative paths
      let pathOnly;
      if (path.startsWith('http')) {
        // For full URLs, parse the URL and get the pathname
        const url = new URL(path);
        pathOnly = url.pathname;
      } else {
        // For relative paths, use as is
        pathOnly = path;
      }
      
      // Remove leading and trailing slashes
      pathOnly = pathOnly.replace(/^\/|\/$/g, '');
      
      // Split the path into segments
      const segments = pathOnly.split('/');
      
      // Extract city, community, and property name based on position
      // Path structure: /city-name/community-name/property-name
      const city = segments.length > 0 ? formatPathSegment(segments[0]) : '';
      const community = segments.length > 1 ? formatPathSegment(segments[1]) : '';
      const propertyName = segments.length > 2 ? formatPathSegment(segments[2]) : '';
      
      return { 
        city, 
        community, 
        propertyName,
        // Also return the normalized slugs for matching
        citySlug: segments.length > 0 ? segments[0] : '',
        communitySlug: segments.length > 1 ? segments[1] : ''
      };
    } catch (error) {
      console.error('Error parsing path:', error);
      return { city: '', community: '', propertyName: '', citySlug: '', communitySlug: '' };
    }
  };
  
  // Format a path segment to readable text (convert slug to title case)
  const formatPathSegment = (segment) => {
    if (!segment) return '';
    
    // Convert slug format (e.g., "dubai-hills-estate") to title case ("Dubai Hills Estate")
    return segment
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Function to check if community exists and has a bird image
  const checkCommunityHasBird = (path) => {
    if (!path) return false;
    
    try {
      // Extract path components for more reliable parsing
      const { citySlug, communitySlug } = extractPathComponents(path);
      
      if (!citySlug || !communitySlug) return false;
      
      // Construct the normalized community path
      const communityPath = `/${citySlug}/${communitySlug}`;
      
      // Find the community by matching the path
      const community = communities.find(c => {
        // Normalize the community path for comparison
        const normalizedCommunityPath = c.path ? 
          c.path.replace(/\/+$/, '') : ''; // Remove trailing slashes
        
        return normalizedCommunityPath.toLowerCase() === communityPath.toLowerCase();
      });
      
      // Check if community exists AND has a valid bird property
      if (!community) {
        console.log(`Community not found for path: ${communityPath}`);
        return false;
      }
      
      return !!community.bird;
    } catch (error) {
      console.error('Error checking community bird:', error);
      return false;
    }
  };

  const loading = propertiesLoading || groupPropertiesLoading;
  const error = propertiesError || groupPropertiesError;

  // CSS for scrollable table
  const tableContainerStyle = {
    maxHeight: 'calc(100vh - 350px)',
    overflowY: 'auto',
    overflowX: 'auto',
    marginBottom: '20px'
  };
  
  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed'
  };
  
  const stickyHeaderStyle = {
    position: 'sticky',
    top: 0,
    backgroundColor: '#f8f9fa',
    zIndex: 1,
    boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
  };

  const tableCellStyle = {
    padding: '8px',
    verticalAlign: 'middle',
    height: '60px'
  };

  const logoCellStyle = {
    ...tableCellStyle,
    textAlign: 'center'
  };

  const actionsCellStyle = {
    ...tableCellStyle,
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const actionButtonStyle = {
    margin: '0 5px'
  };

  const logoImageStyle = {
    maxHeight: '40px',
    maxWidth: '80px',
    objectFit: 'contain',
    display: 'block',
    margin: '0 auto'
  };

  const truncatedCellStyle = {
    ...tableCellStyle,
    maxWidth: '250px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  };

  const handleCityChange = (e) => {
    const selectedCityId = e.target.value ? parseInt(e.target.value, 10) : null;
    setCityId(selectedCityId);
    setCommunityId(null); // Reset community selection when city changes
  };

  const handleCommunityChange = (e) => {
    const selectedCommunityId = e.target.value ? parseInt(e.target.value, 10) : null;
    setCommunityId(selectedCommunityId);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleEdit = (item) => {
    // Set page transition state to prevent loading indicator
    setIsPageTransition(true);
    navigate(`/admin/properties/${item.itemType}/${item.id}`);
  };

  const handleView = (item) => {
    const pathToView = item.path;
    
    if (!pathToView) {
      alert("This property doesn't have a valid path to display");
      return;
    }
    
    // Check if the community exists and has a bird image
    const communityValid = checkCommunityHasBird(pathToView);
    
    if (!communityValid) {
      alert("This property's community doesn't exist or doesn't have a bird image");
      return;
    }
    
    window.open(pathToView, '_blank');
  };

  const handleDelete = async (itemId, itemType) => {
    if (window.confirm("Are you sure you want to delete this property?")) {
      try {
        if (itemType === 'property') {
          await deleteProperty(itemId);
          fetchProperties();
        } else if (itemType === 'groupProperty') {
          await deleteGroupProperty(itemId);
          fetchGroupProperties();
        }
      } catch (err) {
        console.error("Error deleting item:", err);
      }
    }
  };

  const searchInputStyle = {
    width: '100%',
    padding: '8px',
    marginBottom: '15px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '16px'
  };

  return (
    <PageTransition>
      <div className="properties-manager">
        <h1>Manage Properties</h1>
        
        {/* Search input - second row */}
        <div className="search-container">
          <input
            type="text"
            placeholder="Search properties (by name, initials, or partial words)"
            value={searchTerm}
            onChange={handleSearchChange}
            style={searchInputStyle}
          />
        </div>
        
        {/* Filters - third row */}
        <div className="filter-controls">
          <div className="form-group">
            <label htmlFor="city-filter">Filter by City:</label>
            <select
              id="city-filter"
              value={cityId || ""}
              onChange={handleCityChange}
            >
              <option value="">All Cities</option>
              {cities.map((city) => (
                <option key={city.id} value={city.id}>
                  {city.name}
                </option>
              ))}
            </select>
          </div>
          
          {cityId && (
            <div className="form-group">
              <label htmlFor="community-filter">Filter by Community:</label>
              <select
                id="community-filter"
                value={communityId || ""}
                onChange={handleCommunityChange}
                disabled={!cityId}
              >
                <option value="">All Communities</option>
                {communities.map((community) => (
                  <option key={community.id} value={community.id}>
                    {community.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        
        {error && <div className="admin-error">{error}</div>}
        
        {loading ? (
          <div className="loading">Loading properties and group properties...</div>
        ) : (
          <div className="admin-table-container" style={tableContainerStyle}>
            {filteredItems.length === 0 ? (
              <div className="admin-empty-state">
                {searchTerm ? "No properties found matching your search." : "No properties or group properties found with the current filters."}
              </div>
            ) : (
              <table className="admin-table" style={tableStyle}>
                <thead>
                  <tr style={stickyHeaderStyle}>
                    <th style={tableCellStyle}>Name</th>
                    {!cityId && <th style={tableCellStyle}>City</th>}
                    {!communityId && <th style={tableCellStyle}>Community</th>}
                    <th style={tableCellStyle}>Link</th>
                    <th style={logoCellStyle}>Logo</th>
                    <th style={actionsCellStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item) => {
                    const pathComponents = extractPathComponents(item.path);
                    
                    // Extract city and community IDs from the item
                    let itemCityId = null;
                    let itemCommunityId = null;
                    
                    if (item.itemType === 'property') {
                      // For regular properties
                      itemCommunityId = item.community_id;
                      
                      // Find the city ID through the community if available
                      if (itemCommunityId) {
                        const community = communities.find(c => c.id === itemCommunityId);
                        if (community) {
                          itemCityId = community.city_id;
                        }
                      }
                    } else if (item.itemType === 'groupProperty') {
                      // For group properties, city ID might be directly available
                      itemCityId = item.city_id;
                      
                      // Community ID might be available directly or we may need to infer it
                      itemCommunityId = item.community_id;
                      
                      // If we don't have community ID but have a path, try to infer the community
                      if (!itemCommunityId && item.path) {
                        const { citySlug, communitySlug } = pathComponents;
                        
                        if (communitySlug) {
                          // Try to find the community by its path
                          const matchingCommunity = communities.find(c => {
                            const commPath = c.path ? c.path.replace(/^\/|\/$/g, '').split('/') : [];
                            return commPath.length > 1 && 
                              commPath[1].toLowerCase() === communitySlug.toLowerCase() &&
                              (!itemCityId || c.city_id === itemCityId);
                          });
                          
                          if (matchingCommunity) {
                            itemCommunityId = matchingCommunity.id;
                            if (!itemCityId) {
                              itemCityId = matchingCommunity.city_id;
                            }
                          }
                        }
                      }
                    }
                    
                    return (
                      <tr key={`${item.itemType}-${item.id}`}>
                        <td style={truncatedCellStyle}>{item.name}</td>
                        {!cityId && <td style={truncatedCellStyle}>
                          {getCityName(itemCityId) || pathComponents.city}
                        </td>}
                        {!communityId && <td style={truncatedCellStyle}>
                          {getCommunityName(itemCommunityId, itemCityId) || pathComponents.community}
                        </td>}
                        <td style={truncatedCellStyle}>{item.link || "-"}</td>
                        <td style={logoCellStyle}>
                          {item.logo ? (
                            <img 
                              src={getFileUrl(item.logo)} 
                              alt="Logo" 
                              style={logoImageStyle} 
                            />
                          ) : '-'}
                        </td>
                        <td style={actionsCellStyle} className="table-actions">
                          <button
                            className="edit-btn"
                            onClick={() => handleEdit(item)}
                            title="Edit"
                            style={actionButtonStyle}
                          >
                            ✏️
                          </button>
                          <button
                            className="view-btn"
                            onClick={() => handleView(item)}
                            title={checkCommunityHasBird(item.path) ? "View in App" : "Community missing or has no image"}
                            style={{
                              ...actionButtonStyle,
                              opacity: checkCommunityHasBird(item.path) ? 1 : 0.5,
                              cursor: checkCommunityHasBird(item.path) ? 'pointer' : 'not-allowed'
                            }}
                          >
                            👁️
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </PageTransition>
  );
};

export default PropertiesManager; 